import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Trash2, Loader2, QrCode, Copy, Link as LinkIcon } from "lucide-react";

export default function QRCodeManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: "", url: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/qrcodes");
      setItems(data.qrcodes || []);
    } catch {
      toast.error("Không tải được danh sách mã QR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const url = form.url.trim();
    if (!url) { toast.error("Vui lòng nhập link"); return; }
    setCreating(true);
    try {
      await api.post("/qrcodes", { label: form.label.trim(), url });
      toast.success("Đã tạo mã QR");
      setForm({ label: "", url: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tạo mã QR");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Xoá mã QR "${label || "chưa đặt tên"}"?`)) return;
    try {
      await api.delete(`/qrcodes/${id}`);
      toast.success("Đã xoá mã QR");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xoá");
    }
  };

  const handleDownload = async (item, format = "png") => {
    // PNG: dùng data URL đã có sẵn (nhanh, không cần request). SVG/EPS: gọi backend.
    if (format === "png") {
      const a = document.createElement("a");
      a.href = item.png_data_url;
      const safeLabel = (item.label || "qrcode").replace(/[^\p{L}\p{N}_-]+/gu, "_");
      a.download = `${safeLabel}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    try {
      const resp = await api.get(`/qrcodes/${item.id}/${format}`, { responseType: "blob" });
      const blob = new Blob([resp.data], {
        type: format === "svg" ? "image/svg+xml" : "application/postscript",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeLabel = (item.label || "qrcode").replace(/[^\p{L}\p{N}_-]+/gu, "_");
      a.download = `${safeLabel}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`Không tải được file ${format.toUpperCase()}`);
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Đã copy link");
    } catch {
      toast.error("Không copy được");
    }
  };

  return (
    <div className="space-y-6" data-testid="qrcode-manager">
      {/* Create form */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-4">
          <QrCode className="w-5 h-5 text-lime-400" />
          <h3 className="text-base font-heading font-semibold text-[#F8FAFC]">Tạo mã QR mới</h3>
        </div>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tên gợi nhớ</label>
            <Input
              value={form.label}
              onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="VD: Menu quán / Wifi..."
              className="bg-[#0f172a] border-[#334155] text-slate-200"
              data-testid="qrcode-label-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Link *</label>
            <Input
              value={form.url}
              onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
              placeholder="https://karinox.com/..."
              className="bg-[#0f172a] border-[#334155] text-slate-200"
              data-testid="qrcode-url-input"
              required
            />
          </div>
          <Button
            type="submit"
            disabled={creating}
            className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold h-10 whitespace-nowrap"
            data-testid="qrcode-create-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tạo QR"}
          </Button>
        </form>
        <p className="text-[11px] text-slate-500 mt-2">
          Mã QR được lưu vĩnh viễn, có thể tải PNG bất kỳ lúc nào.
        </p>
      </div>

      {/* List */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-heading font-semibold text-[#F8FAFC]">
            Danh sách mã QR
          </h3>
          <Badge className="bg-slate-700/60 text-slate-300 border-slate-600 border">
            {items.length} mã
          </Badge>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-lime-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-sm">
            <QrCode className="w-10 h-10 mx-auto mb-2 opacity-40" />
            Chưa có mã QR nào. Tạo mã đầu tiên bằng form phía trên.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => (
              <div
                key={item.id}
                className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 flex flex-col gap-3"
                data-testid={`qrcode-item-${item.id}`}
              >
                <div className="flex items-center justify-center bg-white rounded-md p-2">
                  <img
                    src={item.png_data_url}
                    alt={item.label || "QR"}
                    className="w-full max-w-[200px] aspect-square object-contain"
                  />
                </div>
                <div className="space-y-1 min-w-0">
                  <div className="text-sm font-medium text-slate-100 truncate" title={item.label || "(chưa đặt tên)"}>
                    {item.label || <span className="italic text-slate-500">(chưa đặt tên)</span>}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-sky-400 truncate">
                    <LinkIcon className="w-3 h-3 shrink-0" />
                    <a href={item.url} target="_blank" rel="noreferrer" className="truncate hover:underline" title={item.url}>
                      {item.url}
                    </a>
                  </div>
                  <div className="text-[10px] text-slate-600">
                    {item.created_at ? new Date(item.created_at).toLocaleString("vi-VN") : ""}
                  </div>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => handleDownload(item, "png")}
                    className="flex-1 bg-lime-500/15 border border-lime-500/30 text-lime-400 hover:bg-lime-500/25 h-8 text-xs gap-1"
                    data-testid={`qrcode-download-${item.id}`}
                    title="Tải PNG (raster)"
                  >
                    <Download className="w-3.5 h-3.5" /> PNG
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDownload(item, "svg")}
                    className="flex-1 bg-sky-500/15 border border-sky-500/30 text-sky-400 hover:bg-sky-500/25 h-8 text-xs gap-1"
                    data-testid={`qrcode-download-svg-${item.id}`}
                    title="Tải SVG vector — không vỡ khi phóng lớn"
                  >
                    <Download className="w-3.5 h-3.5" /> SVG
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDownload(item, "eps")}
                    className="flex-1 bg-purple-500/15 border border-purple-500/30 text-purple-400 hover:bg-purple-500/25 h-8 text-xs gap-1"
                    data-testid={`qrcode-download-eps-${item.id}`}
                    title="Tải EPS vector — cho Illustrator/CorelDraw, in khổ lớn"
                  >
                    <Download className="w-3.5 h-3.5" /> EPS
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCopyLink(item.url)}
                    className="border-[#334155] text-slate-300 hover:bg-[#334155] h-8 text-xs gap-1"
                    data-testid={`qrcode-copy-${item.id}`}
                    title="Copy link"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(item.id, item.label)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs"
                    data-testid={`qrcode-delete-${item.id}`}
                    title="Xoá"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
